import { IntegrationCardDetailsText } from '@modules/integrationsV2/components/IntegrationCardDetailsText';
import { DisabledIntegrationIndicator } from '@modules/integrationsV2/components/IntegrationStateIndicator';
import { IntegrationSource } from '@modules/integrationsV2/types';
import { getDisplayNameForIntegrationType } from '@modules/integrationsV2/utils';
import { Stack, Typography } from '@mui/material';
import {
  IntegrationChannelType,
  IntegrationViewFragment,
} from 'generated/graphql';

interface IntegrationSourceTitleProps {
  integrationSource: IntegrationSource;
  integrations: IntegrationViewFragment[];
}

export const IntegrationSourceTitle = ({
  integrationSource,
  integrations,
}: IntegrationSourceTitleProps) => {
  const {
    integrationType,
    channel: { displayName, type: channelType },
    namespace,
    isSingleConnectionSource,
    description,
  } = integrationSource;

  const currentIntegration = isSingleConnectionSource
    ? integrations.find(
        (integration) => integration.id === integrationSource.integrationID,
      )
    : null;

  const renderIntegrationSecondaryDetails = () => {
    if (
      !currentIntegration ||
      channelType === IntegrationChannelType.FileUpload
    ) {
      return null;
    }

    if (channelType === IntegrationChannelType.Snowflake) {
      return (
        <IntegrationCardDetailsText
          prefixText="Public Key:"
          text={currentIntegration.data.snowflake?.publicKey}
          variant="caption"
          color="text.secondary"
          maxWidth="400px"
          showClickToCopyTooltip
        />
      );
    }

    return (
      <IntegrationCardDetailsText
        prefixText="API Key:"
        text={currentIntegration.externalID}
        variant="caption"
        color="text.secondary"
        maxWidth="400px"
        showClickToCopyTooltip
      />
    );
  };

  return (
    <Stack rowGap={0.5}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography variant="h6">{displayName}</Typography>
        <Typography variant="body2" color="text.secondary">
          {getDisplayNameForIntegrationType(integrationType)}{' '}
          {isSingleConnectionSource ? 'Integration' : 'Integrations'}
        </Typography>
        {integrationSource.isDisabled && (
          <DisabledIntegrationIndicator variant="chip" />
        )}
      </Stack>
      {namespace && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {namespace}
        </Typography>
      )}
      {renderIntegrationSecondaryDetails()}
      {description && (
        <Typography variant="body2" color="text.secondary">
          Description: {description}
        </Typography>
      )}
    </Stack>
  );
};
